/**
 * Payload for registering a clinic event with Engage.
 * PATCH /api/v1/engage/events/clinic/{referralId}
 */
export interface EngageClinicEventPayload {
  eventType: EngageClinicEventType;
  metadata?: Record<string, unknown>;
  /** PHI patient id (optional body field when path uses Engage referral id) */
  patientId?: string;
  advisorId?: string;
  triggeredBy?: string;
  encounterId?: string;
  providerId?: string;
  dos?: string;
  assignedAdvisor?: string;
  careTeam?: string[];
  chartsScheduledPhysician?: string;
}

/**
 * Engage clinic event types.
 * Use these when calling EngageEventsService.registerClinicEvent().
 * Registration points (when to fire each event) to be defined later.
 */
export const EngageClinicEventType = {
  /** New lead - with referral source (Online / Dr. Tamam or Dr. Kaki) */
  NewLead: 'new_lead',
  /** Contacted = First phone - call or SMS, with no response */
  Contacted: 'contacted',
  /** Reached = Answered Call - replied to a message */
  Reached: 'reached',
  /** Clinic scheduled (new schedule) */
  ClinicScheduled: 'clinic_scheduled',
  /** Clinic rescheduled */
  ClinicRescheduled: 'clinic_rescheduled',
  /** Established patient / visit (e.g. new vs established) */
  Established: 'established',
  /** Appointment booked */
  Booked: 'booked',
  /** Goal / milestone achieved */
  Achieved: 'achieved',
  /** Appointment cancelled (reason: cancel from cancel modal) */
  Canceled: 'canceled',
  /** Patient no-show (reason: no_show from cancel modal) */
  NoShow: 'no_show',
  /** Dropped Clinic - Unconfirmed, Cancelled, No Show */
  DroppedClinic: 'dropped_clinic',
  /** Clinic Complete - Clinic Provider and other user = Care Team + Payer details */
  ClinicComplete: 'clinic_complete',
  /** Qualified Encounter Complete with TAME in Context */
  QualifiedEncounterCompleteTame: 'qualified_encounter_complete_tame',
  /** Treatment Schedule / Reschedules - Has a DOS in Charts (later than Clinic Complete date) + Payer details */
  TreatmentScheduledRescheduled: 'treatment_scheduled_rescheduled',
  /** Prior Auth - Charts - Auth Status / if any */
  PriorAuth: 'prior_auth',
  /** Waitlist - Charts - Prior auth setting to be added */
  Waitlist: 'waitlist',
  /** Dropped Procedure - Unconfirmed, Cancelled, No Show */
  DroppedProcedure: 'dropped_procedure',
  /** Treatment - Send name of performing physician, check in and check out time */
  Treatment: 'treatment',
} as const;

export type EngageClinicEventType = (typeof EngageClinicEventType)[keyof typeof EngageClinicEventType];

/** Fields live on `eventData` in GET /api/v1/engage/events/by-referral/{referralId} */
export interface EngageReferralEventDataPayload {
  eventId?: string;
  eventType?: string;
  /** ISO datetime string */
  eventDateAndTime?: string;
  eventCreatedBy?: string | null;
  eventDetails?: Record<string, unknown> | null;
  LeadStatus?: string;
  leadStatus?: string;
  [key: string]: unknown;
}

/**
 * Response shape for GET /api/v1/engage/events/by-referral/{referralId}
 * `eventData` is an object (single event) or an array of event payloads.
 */
export interface EngageEventsByReferralResponse {
  eventData?: EngageReferralEventDataPayload | EngageReferralEventDataPayload[];
  leadStatus?: string;
  [key: string]: any;
}

/** One row in the timeline: API root, a list element, or legacy flat shape */
export interface EngageEventByReferralItem {
  eventData?: EngageReferralEventDataPayload | EngageReferralEventDataPayload[] | null;
  eventId?: string;
  eventType?: string;
  eventDateAndTime?: string;
  eventCreatedBy?: string | null;
  eventCreatedById?: string | null;
  eventDetails?: Record<string, unknown> | null;
  /** Legacy nested shape */
  event?: {
    eventId?: string;
    eventType?: string;
    eventDateAndTime?: string;
    eventDetails?: Record<string, unknown> | null;
    [key: string]: any;
  } | null;
  interests?: unknown;
  [key: string]: any;
}
